import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { format, parseISO } from 'date-fns';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { CalendarList, type DateData } from 'react-native-calendars';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { formatAwayDay, isDayTappable, type RangeSelection } from '@/util/awaySchedule';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * How far ahead the list scrolls. The rules allow a period to start any time ahead, so this is
 * a practical ceiling rather than a rule.
 */
const MONTHS_AHEAD = 24;

const DAY_HEIGHT = 48;

const MONTH_TITLE_HEIGHT = 40;

/**
 * Every month takes the height of a six week month. The list lays months out at one fixed
 * height, and a month that grew or shrank would put the scroll offset out of step with what is
 * on screen. A five week month therefore ends with one empty row.
 */
const MONTH_HEIGHT = MONTH_TITLE_HEIGHT + 6 * DAY_HEIGHT;

type AwayCalendarProps = {
  selection: RangeSelection;
  today: string;
  /** The block already began, so its start is fixed and only the last day moves. */
  locked: boolean;
  /** The block being edited, which sets how long its last day may run. */
  editing?: { startDate: string; endDate: string };
  /** The expert's other blocks, whose days cannot be picked. */
  otherBlocks?: { startDate: string; endDate: string }[];
  onDayPress: (day: string) => void;
  /** A tap on a day the range rules out, so the caller can say why rather than ignore it. */
  onBlockedDayPress?: (day: string) => void;
};

type CalendarInputs = AwayCalendarProps;

/**
 * Packs how a day looks into one short string: tappable, start, last day, inside the range,
 * today. A string compares by value, so a day re-renders only when its own look changes.
 */
function getDayKey(day: string, inputs: CalendarInputs): string {
  const { selection, today, locked, editing, otherBlocks } = inputs;

  const { start, end } = selection;

  const isStart = day === start;

  const isEnd = day === end;

  const inRange = !!start && !!end && day >= start && day <= end;

  return [
    isDayTappable(selection, day, today, locked, editing, otherBlocks),
    isStart,
    isEnd,
    inRange,
    day === today,
  ]
    .map(Number)
    .join('');
}

function readDayKey(key: string) {
  return {
    enabled: key[0] === '1',
    isStart: key[1] === '1',
    isEnd: key[2] === '1',
    inRange: key[3] === '1',
    isToday: key[4] === '1',
  };
}

/**
 * Holds the calendar's inputs outside React's tree so a tap re-renders only the days whose look
 * changed. Passing the selection down as props would re-render every day of every month on
 * each tap, which took about a second on a phone.
 */
function createDayStore(initial: CalendarInputs) {
  let inputs = initial;

  const listeners = new Set<() => void>();

  return {
    getInputs: () => inputs,
    setInputs(next: CalendarInputs) {
      inputs = next;

      listeners.forEach((listener) => listener());
    },
    subscribe(listener: () => void) {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    press(day: string) {
      const { selection, today, locked, editing, otherBlocks, onDayPress, onBlockedDayPress } =
        inputs;

      if (isDayTappable(selection, day, today, locked, editing, otherBlocks)) {
        onDayPress(day);
      } else {
        onBlockedDayPress?.(day);
      }
    },
  };
}

type DayStore = ReturnType<typeof createDayStore>;

const DayStoreContext = createContext<DayStore | null>(null);

function getBandEdges(isStart: boolean, isEnd: boolean): string {
  if (isStart) {
    return 'left-1/2 right-0';
  }

  if (isEnd) {
    return 'left-0 right-1/2';
  }

  return 'left-0 right-0';
}

/**
 * One day, drawn in place of the library's own so it uses the app's tokens and works in both
 * themes. It must stay a stable component: the list would remount every day if its identity
 * changed between renders, so it reads the selection from the store and never from props.
 */
function AwayDay({ date }: { date?: DateData }) {
  const store = useContext(DayStoreContext);

  const day = date?.dateString ?? '';

  const key = useSyncExternalStore(store?.subscribe ?? noopSubscribe, () =>
    store ? getDayKey(day, store.getInputs()) : '00000'
  );

  if (!store || !day) {
    return null;
  }

  const { enabled, isStart, isEnd, inRange, isToday } = readDayKey(key);

  const { today } = store.getInputs();

  const isEdge = isStart || isEnd;

  const showBand = inRange && !(isStart && isEnd);

  const hasBlockedHandler = !!store.getInputs().onBlockedDayPress;

  return (
    <Pressable
      disabled={!enabled && !hasBlockedHandler}
      onPress={() => store.press(day)}
      accessibilityRole="button"
      accessibilityState={{ disabled: !enabled, selected: isEdge }}
      accessibilityLabel={[
        formatAwayDay(day, today),
        isToday ? 'today' : '',
        isStart ? 'start' : '',
        isEnd ? 'last day away' : '',
        enabled ? '' : 'unavailable',
      ]
        .filter(Boolean)
        .join(', ')}
      style={{ height: DAY_HEIGHT }}
      className="w-full items-center justify-center data-[disabled=true]:opacity-100">
      {showBand ? (
        <View
          className={twJoin(
            'absolute bottom-1 top-1 bg-secondary-100',
            getBandEdges(isStart, isEnd)
          )}
        />
      ) : null}

      <View
        className={twJoin(
          'h-10 w-10 items-center justify-center rounded-full',
          isEdge && 'bg-secondary-600',
          isToday && 'border border-secondary-700'
        )}>
        <Text
          className={twJoin(
            'text-lg',
            isEdge ? 'font-bold text-typography-0' : 'font-medium text-typography-900',
            isToday && !isEdge && 'font-semibold text-secondary-700',
            !enabled && !isEdge && 'font-normal text-typography-600'
          )}>
          {Number(day.slice(8))}
        </Text>
      </View>
    </Pressable>
  );
}

function noopSubscribe() {
  return () => undefined;
}

type MonthDate = { toString: (pattern: string) => string };

/**
 * The library's header slot sizes to its content and sits at the start edge, which on web left
 * a long month name clipped at its left edge. Taking the calendar's measured width keeps the
 * title centered over the grid on every platform.
 */
function renderMonthTitle(date: MonthDate | undefined, width: number) {
  if (!date) {
    return null;
  }

  return (
    <View className="justify-end pb-1" style={{ height: MONTH_TITLE_HEIGHT, width }}>
      <Text className="text-center font-bold text-lg text-typography-900">
        {format(parseISO(date.toString('yyyy-MM-dd')), 'MMMM yyyy')}
      </Text>
    </View>
  );
}

/**
 * The library draws the grid, the rest is ours: the day cells, the month title and one weekday
 * row above the list, so the labels do not repeat in every month. Padding and margins are
 * zeroed so the weekday columns line up with the day columns. Which days can be tapped comes
 * from `util/awaySchedule.ts`, never from the library.
 */
const CALENDAR_THEME = {
  calendarBackground: 'transparent',
  weekVerticalMargin: 0,
  'stylesheet.calendar.main': {
    container: { paddingLeft: 0, paddingRight: 0 },
    week: { marginVertical: 0, flexDirection: 'row' },
  },
  'stylesheet.calendar.header': {
    header: { paddingLeft: 0, paddingRight: 0, marginTop: 0, flexDirection: 'row' },
  },
};

type CalendarListHandle = {
  scrollToDay: (date: string, offset: number, animated: boolean) => void;
};

const CALENDAR_STYLE = { paddingLeft: 0, paddingRight: 0 };

/**
 * On web the month list spans the sheet edge to edge, so its scrollbar sits on the sheet's
 * border rather than against the Saturday column. The grid is the header's width and starts
 * under it, so the day columns stay under the weekday letters; on the right that leaves the
 * 8px scrollbar drawn in global.css plus 8px of space before the dates. This must match the
 * sheet's side padding.
 */
const WEB_SHEET_PADDING = 16;

const WEB_LIST_CONTENT_STYLE = { paddingLeft: WEB_SHEET_PADDING };

/**
 * Range calendar over plain `YYYY-MM-DD` days, as months that scroll down. The caller owns the
 * range; the calendar only draws it and reports taps.
 */
export function AwayCalendar(props: AwayCalendarProps) {
  const { selection, today } = props;

  const [width, setWidth] = useState(0);

  const listRef = useRef<CalendarListHandle>(null);

  /**
   * Where the list first opens. It starts at today's month and scrolls to the start of a
   * period in a later month once, on mount. Passing the selection's start as `current` would
   * hide the months before it, since the list begins there, and would scroll the list on
   * every new start the expert taps.
   */
  const initialStart = useRef(selection.start);

  useEffect(() => {
    const opensAt = initialStart.current;

    if (width > 0 && opensAt && opensAt.slice(0, 7) > today.slice(0, 7)) {
      listRef.current?.scrollToDay(opensAt, 0, false);
    }
  }, [width, today]);

  const store = useMemo(() => createDayStore(props), []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    store.setInputs(props);
  });

  const gridWidth = isWeb ? width - WEB_SHEET_PADDING * 2 : width;

  return (
    <DayStoreContext.Provider value={store}>
      <View
        className={twJoin('overflow-hidden', isWeb ? '-mx-4 h-[360px]' : 'min-h-0 w-full flex-1')}
        onLayout={(event) => setWidth(Math.floor(event.nativeEvent.layout.width))}>
        <HStack
          aria-hidden
          importantForAccessibility="no-hide-descendants"
          className={twJoin('mt-2 border-b border-outline-200', isWeb && 'mx-4')}>
          {WEEKDAYS.map((weekday, index) => (
            <Text
              key={index}
              size="sm"
              className="flex-1 pb-2 text-center font-semibold text-secondary-700">
              {weekday}
            </Text>
          ))}
        </HStack>

        {gridWidth > 0 ? (
          <CalendarList
            ref={listRef}
            key={gridWidth}
            testID="away-calendar"
            showScrollIndicator={isWeb}
            contentContainerStyle={isWeb ? WEB_LIST_CONTENT_STYLE : undefined}
            calendarWidth={gridWidth}
            calendarHeight={MONTH_HEIGHT}
            calendarStyle={CALENDAR_STYLE}
            minDate={today}
            current={today}
            pastScrollRange={0}
            futureScrollRange={MONTHS_AHEAD}
            hideDayNames
            hideExtraDays
            firstDay={0}
            dayComponent={AwayDay}
            renderHeader={(date?: MonthDate) => renderMonthTitle(date, gridWidth)}
            theme={CALENDAR_THEME}
          />
        ) : null}
      </View>
    </DayStoreContext.Provider>
  );
}
